import { MessageCircle, Send, X } from "lucide-react";
import { useState } from "react";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../lib/api";
import { Button } from "./button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Textarea } from "./textarea";

interface FeedbackModalProps {
    onClose: () => void;
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
    const [feedbackType, setFeedbackType] = useState<string>("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [validationError, setValidationError] = useState<string>("");
    const { toast } = useToast();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError("");
        setIsSubmitting(true);

        // Client-side validation
        if (message.trim().length < 10) {
            setValidationError("Message must be at least 10 characters long");
            setIsSubmitting(false);
            return;
        }

        try {
            await apiClient.submitFeedback({
                feedbackType: feedbackType || "General Feedback",
                message,
            });

            setIsSubmitted(true);

            // Reset form after a delay
            setTimeout(() => {
                setIsSubmitted(false);
                setFeedbackType("");
                setMessage("");
                setValidationError("");
                onClose();
            }, 2000);
        } catch (error) {
            console.error("Feedback submission failed:", error);

            // Check if it's a validation error from server
            let errorMessage = "Failed to send feedback. Please try again.";

            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (error && typeof error === "object" && "response" in error) {
                const apiError = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
                errorMessage = apiError.response?.data?.error?.message || apiError.message || errorMessage;
            }

            if (errorMessage.toLowerCase().includes("characters") || errorMessage.toLowerCase().includes("required")) {
                setValidationError(errorMessage);
            } else {
                // Only show toast for non-validation errors (network, server errors)
                toast({
                    title: "Feedback Failed",
                    description: errorMessage,
                    variant: "destructive",
                });
            }

            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative page-gradient md:bg-background border border-border md:border-primary/20 rounded-lg shadow-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2 text-primary">
                        <MessageCircle className="h-5 w-5" />
                        <h2 className="text-lg font-semibold">Send Feedback</h2>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Content */}
                <div className="p-4">
                    {isSubmitted ? (
                        <div className="text-center py-8">
                            <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Send className="h-6 w-6 text-success" />
                            </div>
                            <h3 className="font-semibold text-success mb-2">Thank you!</h3>
                            <p className="text-sm text-muted-foreground">
                                Your feedback has been sent. We'll get back to you soon!
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">What type of feedback?</label>
                                <Select value={feedbackType} onValueChange={setFeedbackType}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select feedback type..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Bug Report">🐛 Bug Report</SelectItem>
                                        <SelectItem value="Feature Request">✨ Feature Request</SelectItem>
                                        <SelectItem value="General Feedback">💬 General Feedback</SelectItem>
                                        <SelectItem value="Technical Issue">⚙️ Technical Issue</SelectItem>
                                        <SelectItem value="Content Suggestion">📝 Content Suggestion</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Your message *</label>
                                <Textarea
                                    placeholder="Tell us what's on your mind..."
                                    value={message}
                                    onChange={(e) => {
                                        setMessage(e.target.value);
                                        if (validationError) setValidationError("");
                                    }}
                                    required
                                    rows={4}
                                    className={
                                        validationError ? "border-destructive focus-visible:ring-destructive" : ""
                                    }
                                />
                                {validationError && <p className="text-sm text-destructive">{validationError}</p>}
                            </div>

                            <div className="flex gap-2 pt-2">
                                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={!message.trim() || isSubmitting} className="flex-1">
                                    {isSubmitting ? (
                                        <>Sending...</>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4 mr-2" />
                                            Send Feedback
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
